import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Form.css';
import AppIcon from '../common/AppIcon';
import { getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Form = ({ setLoading, setSuccess, refreshCalendar }) => {
  const [fullName, setFullName] = useState('');
  const [shiftTime, setShiftTime] = useState('24-Hrs');
  const [customHours, setCustomHours] = useState('');
  const [shiftDay, setShiftDay] = useState('');
  const [shiftDayEnd, setShiftDayEnd] = useState('');
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [newHospitalCode, setNewHospitalCode] = useState('');
  const [newHospitalLabel, setNewHospitalLabel] = useState('');
  const [shiftLocation, setShiftLocation] = useState('MCH');
  const [errorMessage, setErrorMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  const apiUrl = getApiBaseUrl();
  const currentMonthYear = new Date().toISOString().slice(0, 7);

  const loadDoctors = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/doctors`);
      const data = Array.isArray(response.data) ? response.data : [];
      setDoctors(data);
    } catch (error) {
      console.error('Error loading doctors:', error);
      setDoctors([]);
    }
  }, [apiUrl]);

  const loadHospitals = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/hospitals`);
      const data = Array.isArray(response.data) ? response.data : [];
      setHospitals(data);
      if (data.length > 0 && !data.find(h => h.code === shiftLocation)) {
        setShiftLocation(data[0].code);
      }
    } catch (error) {
      console.error('Error loading hospitals:', error);
      setHospitals([]);
    }
  }, [apiUrl, shiftLocation]);

  useEffect(() => {
    const dateInput = document.getElementById('shiftDay');
    if (dateInput) {
      dateInput.setAttribute('min', `${currentMonthYear}-01`);
      dateInput.setAttribute('max', `${currentMonthYear}-${new Date(currentMonthYear.split('-')[0], currentMonthYear.split('-')[1], 0).getDate()}`);
    }
    loadHospitals();
    loadDoctors();
  }, [loadHospitals, loadDoctors, currentMonthYear]);

  useEffect(() => {
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.HOSPITALS, DATA_TYPES.DOCTORS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        loadHospitals();
        loadDoctors();
      }
    });
  }, [loadHospitals, loadDoctors]);

  const validateForm = () => {
    const errors = {};
    
    if (!fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!shiftDay) {
      errors.shiftDay = 'Shift day is required';
    }
    if (shiftDayEnd && shiftDayEnd < shiftDay) {
      errors.shiftDay = 'End date cannot be before start date';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    setErrorMessage('');
    setFormErrors({});
    setAiSuggestions([]);

    let shiftTimeArray;
    if (shiftTime === 'custom' && customHours) {
      const h = Math.max(1, Math.min(24, parseInt(customHours, 10) || 0));
      shiftTimeArray = [h, 0];
    } else {
      switch (shiftTime) {
        case '24-Hrs':
          shiftTimeArray = [24, 0];
          break;
        case '12-Hrs':
          shiftTimeArray = [12, 0];
          break;
        case '8-Hrs':
          shiftTimeArray = [8, 0];
          break;
        default:
          shiftTimeArray = [24, 0];
      }
    }

    const toBackendDate = (isoDate) => `${isoDate.split('-')[2]}-${isoDate.split('-')[1]}-${isoDate.split('-')[0]}`; // dd-mm-yyyy

    const startISO = shiftDay;
    const endISO = shiftDayEnd || shiftDay;
    const startDate = new Date(startISO);
    const endDate = new Date(endISO);

    try {
      // loop over date range inclusive
      let cursor = new Date(startDate);
      while (cursor <= endDate) {
        const iso = cursor.toISOString().split('T')[0];
        const payload = {
          name: fullName,
          shift_time: shiftTimeArray,
          shift_date: toBackendDate(iso),
          location: shiftLocation,
        };
        
        try {
          await axios.post(`${apiUrl}/api/shifts`, {
            name: payload.name,
            shift_time: payload.shift_time,
            shift_date: payload.shift_date,
            location: payload.location
          });
        } catch (error) {
          if (error.response && error.response.status === 409) {
            // Conflict detected - show AI suggestions
            setAiSuggestions(error.response.data.suggestions || []);
            setErrorMessage(error.response.data.message);
            setLoading(false);
            setIsSubmitting(false);
            return;
          }
          throw error;
        }
        
        cursor.setDate(cursor.getDate() + 1);
      }

      // Reset form
      setFullName('');
      setShiftDay('');
      setShiftDayEnd('');
      setCustomHours('');
      setFormErrors({});
      setAiSuggestions([]);
      
      setLoading(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      emitDataChange(DATA_TYPES.SHIFTS);
      refreshCalendar();
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setErrorMessage(error.response.data.message);
        setAiSuggestions(error.response.data.suggestions || []);
      } else {
        setErrorMessage('An error occurred while adding the shift. Please try again.');
      }
      setLoading(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldErrorClass = (fieldName) => {
    return formErrors[fieldName] ? 'error' : '';
  };

  const handleUseSuggestion = (suggestion) => {
    const [day, month, year] = suggestion.date.split('-');
    const isoDate = `${year}-${month}-${day}`;
    setShiftDay(isoDate);
    setAiSuggestions([]);
    setErrorMessage('');
  };

  return (
    <div className={`form-container ${isSubmitting ? 'form-loading' : ''}`}>
      <h2 className="form-title">
        <AppIcon name="form" size={26} strokeWidth={2.25} className="app-icon--primary" />
        Schedule New Shift
      </h2>
      
      <form className="shift-form" onSubmit={handleSubmit}>
        <div className={`form-group ${getFieldErrorClass('fullName')}`}>
          <label htmlFor="fullName">Doctor</label>
          {doctors.length > 0 ? (
            <select
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isSubmitting}
              required
            >
              <option value="">Select a doctor</option>
              {doctors.map((d) => {
                const label = d.name || d.display_name || d.username || `Doctor #${d.id}`;
                return (
                  <option key={d.id || d._id} value={label}>
                    {label}
                  </option>
                );
              })}
            </select>
          ) : (
            <>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter doctor's full name"
                disabled={isSubmitting}
              />
              <small style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                No doctors in database — <a href="#doctors">add one in Doctors tab</a> first.
              </small>
            </>
          )}
          {formErrors.fullName && (
            <div className="error-message">{formErrors.fullName}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="shiftTime">Shift Duration</label>
          <select 
            id="shiftTime"
            value={shiftTime} 
            onChange={(e) => setShiftTime(e.target.value)}
            disabled={isSubmitting}
          >
            <option value="24-Hrs">24 Hours</option>
            <option value="12-Hrs">12 Hours</option>
            <option value="8-Hrs">8 Hours</option>
            <option value="custom">Custom…</option>
          </select>
          {shiftTime === 'custom' && (
            <div style={{marginTop: 8}}>
              <input
                type="number"
                min="1"
                max="24"
                placeholder="Enter hours (1-24)"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        <div className={`form-group ${getFieldErrorClass('shiftDay')}`}>
          <label htmlFor="shiftDay">Shift Date</label>
          <input 
            type="date" 
            id="shiftDay"
            value={shiftDay} 
            onChange={(e) => setShiftDay(e.target.value)} 
            required
            disabled={isSubmitting}
          />
          <div style={{marginTop: 8}}>
            <label htmlFor="shiftDayEnd">End Date (optional)</label>
            <input 
              type="date" 
              id="shiftDayEnd"
              value={shiftDayEnd} 
              onChange={(e) => setShiftDayEnd(e.target.value)} 
              disabled={isSubmitting}
            />
          </div>
          {formErrors.shiftDay && (
            <div className="error-message">{formErrors.shiftDay}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="shiftLocation">Hospital Location</label>
          <select 
            id="shiftLocation"
            value={shiftLocation} 
            onChange={(e) => setShiftLocation(e.target.value)}
            disabled={isSubmitting}
          >
            {hospitals.map(h => (
              <option key={h.code} value={h.code}>{h.name || h.label || h.code}</option>
            ))}
          </select>
          <div style={{marginTop: 8}}>
            <label>Add Hospital</label>
            <div style={{display:'flex', gap:8}}>
              <input
                type="text"
                placeholder="Code (e.g., ABC)"
                value={newHospitalCode}
                onChange={(e) => setNewHospitalCode(e.target.value.toUpperCase())}
                disabled={isSubmitting}
                style={{maxWidth:180}}
              />
              <input
                type="text"
                placeholder="Label (e.g., ABC - New Hospital)"
                value={newHospitalLabel}
                onChange={(e) => setNewHospitalLabel(e.target.value)}
                disabled={isSubmitting}
              />
              <button type="button" onClick={async () => {
                if (!newHospitalCode || !newHospitalLabel) return;
                if (hospitals.find(h => h.code === newHospitalCode)) return;
                
                try {
                  await axios.post(`${apiUrl}/api/hospitals`, { 
                    code: newHospitalCode,
                    name: newHospitalLabel
                  });
                  
                  // Reload hospitals
                  const response = await axios.get(`${apiUrl}/api/hospitals`);
                  setHospitals(Array.isArray(response.data) ? response.data : []);
                  setNewHospitalCode('');
                  setNewHospitalLabel('');
                  emitDataChange(DATA_TYPES.HOSPITALS);
                } catch (error) {
                  console.error('Error adding hospital:', error);
                  alert('Failed to add hospital. Please try again.');
                }
              }}>Add</button>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Scheduling...' : 'Schedule Shift'}
        </button>
      </form>

      {errorMessage && (
        <div className="error-notification">
          <span className="notification-icon">⚠️</span>
          {errorMessage}
        </div>
      )}

      {aiSuggestions.length > 0 && (
        <div className="ai-suggestions">
          <h4>🤖 AI Suggestions - Available Slots:</h4>
          <div className="suggestions-list">
            {aiSuggestions.map((suggestion, index) => (
              <div key={index} className="suggestion-item">
                <div className="suggestion-date">
                  <strong>{suggestion.date}</strong> - {suggestion.available_slots} slot(s) available
                </div>
                {suggestion.existing_shifts.length > 0 && (
                  <div className="existing-shifts">
                    Existing: {suggestion.existing_shifts.map(s => `${s.name} (${s.location})`).join(', ')}
                  </div>
                )}
                <button 
                  className="use-suggestion-btn"
                  onClick={() => handleUseSuggestion(suggestion)}
                >
                  Use This Date
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;